import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ADMIN_NAV_LINKS } from 'components/admin/constants';
import { DISCHARGE_CALENDAR_ROUTES } from 'components/dischargeCalendar/constants';
import { TASKS_ROUTES } from 'components/inboundReferrals/tasks/constants';
import usePortfolioTabs from 'components/portfolio/usePortfolioTabs';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import { FooterDot, PrivacyLink } from 'components/shared/MenuFooterComponents';
import { Flags } from 'constants/flags';
import { useAuth } from 'context/auth';
import { useProfile } from 'context/profile';
import { useReferralTerminology } from 'hooks/useReferralTerminology';
import { capitalize } from 'lib/util';
import { ImportType } from 'models/Client';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import { useMobileNavigationActions, useMobileNavigationStore } from 'stores/mobileNavigationStore';
import { useSelectViewActions } from 'stores/selectViewStore';
import { colors } from 'styles/theme/colors';
import ChevronRight from 'svg/ChevronRight';
import DischargeCalendarIcon from 'svg/DischargeCalendarIcon';
import GearIcon from 'svg/GearIcon';
import LogoutIcon from 'svg/LogoutIcon';
import OlioOLogo from 'svg/OlioOLogo';
import PatientListsIcon from 'svg/PatientListsIcon';
import PatientPortfolioIcon from 'svg/PatientPortfolioIcon';
import PerformanceEngagementIcon from 'svg/PerformanceEngagementIcon';
import PlusIcon from 'svg/PlusIcon';
import ReferralsIcon from 'svg/ReferralIcon';
import SummarizationIcon from 'svg/SummarizationIcon';

export default function MobileNavigation() {
  const { logout } = useAuth();
  const { profile } = useProfile();
  const { listLabel } = useReferralTerminology();
  const navigate = useNavigate();
  const navigationOpen = useMobileNavigationStore((state) => state.navigationOpen);
  const { setNavigationOpen } = useMobileNavigationActions();
  const { setSelectViewOpen } = useSelectViewActions();
  const tabs = usePortfolioTabs();

  const showConnectPatients = useMemo(() => {
    return (
      profile.has(PERMISSIONS.patientCreate) &&
      profile.actingClient.importType === ImportType.IMPORTED_PATIENT &&
      profile.canActAsManager &&
      (profile.isAdmin || (!profile.isPhysicianTeamUser && !profile.isAffiliateUser))
    );
  }, [profile]);

  const showReferrals = profile.actingClient.handlesReferrals;
  const showAdmissions =
    profile.has(PRODUCT_ACCESSORS.admissionsManagementEnabled) && profile.has(PERMISSIONS.reviewAdmissionView);
  const hasHealthPlans = profile.actingClient.providedProviderTypes.some((x) => x.config.designatesAcceptedPlans);
  const showInboundReferrals =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) && profile.hasFlag(Flags.InboundReferrals);
  // Insights keeps riding showInboundReferrals; the queues also need view (OLIO-2615).
  const showReferralQueues = showInboundReferrals && profile.has(PERMISSIONS.workflowInboundReferralView);
  // Read directly rather than through <AccessGuard>: the merged "Referrals" section and the
  // relocated Contracted Health Plans link sit outside that guard (OLIO-2905).
  const hasCareCoordination = profile.has(PRODUCT_ACCESSORS.careCoordinationEnabled);
  const showSnfCustomerInsights = hasCareCoordination && profile.isSnfCustomerUser;
  const showInboundReferralInsights = showInboundReferrals && profile.hasFlag(Flags.InsightsInboundReferrals);

  const adminLinks = ADMIN_NAV_LINKS.flatMap(
    (link) =>
      link.children ??
      (link.to ? [{ to: link.to, title: link.title, accessor: link.accessor, strictMode: link.strictMode }] : [])
  );

  return (
    <Foldout
      data-cy='leftFoldout'
      open={navigationOpen}
      setOpen={setNavigationOpen}
      position='left'
      closeOnBackdropClick>
      <FoldoutHeaderContainer>
        <div>
          <OlioOLogo color={colors.blue100} width={36} height={36} />
        </div>
        <FoldoutHeaderText>
          <UsernameText>
            {profile.fullName}
            {profile.actingClient && <ActingClientName>({profile.actingClient?.name})</ActingClientName>}
          </UsernameText>
          <SubtitleText>{profile.email}</SubtitleText>
        </FoldoutHeaderText>
      </FoldoutHeaderContainer>
      <NavigationWrapper>
        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <FoldoutMenuHeader>
            <FoldoutIconSlot>
              <PatientPortfolioIcon width={16} height={16} color={colors.black100} />
            </FoldoutIconSlot>
            Patient Portfolio
          </FoldoutMenuHeader>
          {tabs.map((tab) => {
            return (
              <FoldoutMenuLink
                key={tab.value}
                onClick={() => {
                  navigate(`/patients?tab=${tab.value}`);
                  setNavigationOpen(false);
                }}>
                {capitalize(tab.displayName)} Patients ({tab.count})
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            );
          })}

          <FoldoutMenuHeader>
            <FoldoutIconSlot>
              <PatientListsIcon width={12} height={12} color={colors.black100} />
            </FoldoutIconSlot>
            Patient Lists
          </FoldoutMenuHeader>
          <FoldoutMenuLink
            onClick={() => {
              navigate('/all-patients');
              setNavigationOpen(false);
            }}>
            All Patients
            <Spacer />
            <FoldoutChevronContainer>
              <ChevronRight width={12} height={12} color={colors.black50} />
            </FoldoutChevronContainer>
          </FoldoutMenuLink>
          <AccessGuard accessor={PRODUCT_ACCESSORS.dischargeCalendarEnabled}>
            <FoldoutMenuHeader>
              <FoldoutIconSlot>
                <DischargeCalendarIcon width={16} height={16} color={colors.black100} />
              </FoldoutIconSlot>
              Census Management
            </FoldoutMenuHeader>
            <FoldoutMenuLink
              data-cy='discharge-calendar'
              onClick={() => {
                navigate(DISCHARGE_CALENDAR_ROUTES.BASE);
                setNavigationOpen(false);
              }}>
              Discharge Calendar
              <Spacer />
              <FoldoutChevronContainer>
                <ChevronRight width={12} height={12} color={colors.black50} />
              </FoldoutChevronContainer>
            </FoldoutMenuLink>
          </AccessGuard>

          {showConnectPatients && !profile.isSnfCustomerUser && (
            <FoldoutMenuLink
              onClick={() => {
                navigate('/coordinate');
                setNavigationOpen(false);
              }}>
              Coordinate
              <Spacer />
              <FoldoutChevronContainer>
                <ChevronRight width={12} height={12} color={colors.black50} />
              </FoldoutChevronContainer>
            </FoldoutMenuLink>
          )}
        </AccessGuard>
        {/*
          One "Referrals" header replacing the old "Transitions of Care" + "Referrals" pair
          (OLIO-2905). Inbound Referrals never needed Care Coordination access, so the two
          items that did — Admissions and the outbound list — check it themselves here rather
          than sitting inside the guard above.
        */}
        {(showReferralQueues || (hasCareCoordination && (showReferrals || showAdmissions))) && (
          <>
            <FoldoutMenuHeader>
              <FoldoutIconSlot>
                <ReferralsIcon width={16} height={16} color={colors.black100} />
              </FoldoutIconSlot>
              Referrals
            </FoldoutMenuHeader>
            {showReferralQueues && (
              <>
                <FoldoutMenuLink
                  onClick={() => {
                    navigate(TASKS_ROUTES.BASE);
                    setNavigationOpen(false);
                  }}>
                  Tasks
                  <Spacer />
                  <FoldoutChevronContainer>
                    <ChevronRight width={12} height={12} color={colors.black50} />
                  </FoldoutChevronContainer>
                </FoldoutMenuLink>
                <FoldoutMenuLink
                  onClick={() => {
                    navigate('/inbound-referrals');
                    setNavigationOpen(false);
                  }}>
                  Inbound Referrals
                  <Spacer />
                  <FoldoutChevronContainer>
                    <ChevronRight width={12} height={12} color={colors.black50} />
                  </FoldoutChevronContainer>
                </FoldoutMenuLink>
              </>
            )}
            {hasCareCoordination && showReferrals && (
              <FoldoutMenuLink
                data-cy='referrals-link'
                onClick={() => {
                  navigate('/referrals');
                  setNavigationOpen(false);
                }}>
                {listLabel}
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
            {hasCareCoordination && showAdmissions && (
              <FoldoutMenuLink
                data-cy='admissions-link'
                onClick={() => {
                  navigate('/admissions');
                  setNavigationOpen(false);
                }}>
                Admissions
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
          </>
        )}
        {/*
          Contracted Health Plans moved out of "Transitions of Care" (OLIO-2905). Desktop has
          a Configuration section already; mobile gains one here. Gating is unchanged —
          Care Coordination access plus a provided provider type that designates plans — and
          the link now points at the registered /contracted-health-plans route (it used to
          navigate to /referrals/contracted-health-plans, which matches no route).
        */}
        {hasCareCoordination && hasHealthPlans && (
          <>
            <FoldoutMenuHeader>
              <FoldoutIconSlot>
                <GearIcon width={16} height={16} color={colors.black100} />
              </FoldoutIconSlot>
              Configuration
            </FoldoutMenuHeader>
            <FoldoutMenuLink
              data-cy='contractedHealthPlansLink'
              onClick={() => {
                navigate('/contracted-health-plans');
                setNavigationOpen(false);
              }}>
              Contracted Health Plans
              <Spacer />
              <FoldoutChevronContainer>
                <ChevronRight width={12} height={12} color={colors.black50} />
              </FoldoutChevronContainer>
            </FoldoutMenuLink>
          </>
        )}
        {(hasCareCoordination || showInboundReferralInsights) && (
          <>
            <FoldoutMenuHeader>
              <FoldoutIconSlot>
                <PerformanceEngagementIcon width={16} height={16} color={colors.black100} />
              </FoldoutIconSlot>
              Insights
            </FoldoutMenuHeader>
            {hasCareCoordination && (
              <FoldoutMenuLink
                onClick={() => {
                  navigate('/insights');
                  setNavigationOpen(false);
                }}>
                {showSnfCustomerInsights ? 'Facility Dashboard' : 'Performance & Engagement'}
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
            {showSnfCustomerInsights && (
              <FoldoutMenuLink
                onClick={() => {
                  navigate('/insights/post-discharge');
                  setNavigationOpen(false);
                }}>
                Post Discharge Dashboard
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
            {showInboundReferralInsights && (
              <FoldoutMenuLink
                onClick={() => {
                  navigate('/insights/inbound-referrals');
                  setNavigationOpen(false);
                }}>
                {showSnfCustomerInsights ? 'Inbound Referrals' : 'Referral Coordination'}
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
            {showSnfCustomerInsights && (
              <FoldoutMenuLink
                onClick={() => {
                  navigate('/insights/referrals');
                  setNavigationOpen(false);
                }}>
                {showInboundReferralInsights ? 'Outbound Referrals' : 'Referral Coordination'}
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
            {showSnfCustomerInsights && (
              <FoldoutMenuLink
                onClick={() => {
                  navigate('/insights/business-development');
                  setNavigationOpen(false);
                }}>
                Business Development
                <Spacer />
                <FoldoutChevronContainer>
                  <ChevronRight width={12} height={12} color={colors.black50} />
                </FoldoutChevronContainer>
              </FoldoutMenuLink>
            )}
          </>
        )}

        <AccessGuard accessor={PRODUCT_ACCESSORS.olioSummarizationEnabled}>
          <FoldoutMenuHeader>
            <FoldoutIconSlot>
              <SummarizationIcon width={16} height={16} color={colors.black100} />
            </FoldoutIconSlot>
            Summarization
          </FoldoutMenuHeader>
          <FoldoutMenuLink
            onClick={() => {
              navigate('/summarizations/new');
              setNavigationOpen(false);
            }}>
            New Summary
            <Spacer />
            <FoldoutChevronContainer>
              <ChevronRight width={12} height={12} color={colors.black50} />
            </FoldoutChevronContainer>
          </FoldoutMenuLink>
          <FoldoutMenuLink
            onClick={() => {
              navigate('/summarizations');
              setNavigationOpen(false);
            }}>
            History
            <Spacer />
            <FoldoutChevronContainer>
              <ChevronRight width={12} height={12} color={colors.black50} />
            </FoldoutChevronContainer>
          </FoldoutMenuLink>
        </AccessGuard>

        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <FoldoutMenuHeader>
            <FoldoutIconSlot>
              <GearIcon width={16} height={16} color={colors.black100} />
            </FoldoutIconSlot>
            Settings
          </FoldoutMenuHeader>
          <FoldoutMenuLink
            onClick={() => {
              navigate('/preferences');
              setNavigationOpen(false);
            }}>
            Notification Preferences
            <Spacer />
            <FoldoutChevronContainer>
              <ChevronRight width={12} height={12} color={colors.black50} />
            </FoldoutChevronContainer>
          </FoldoutMenuLink>
        </AccessGuard>
        {profile.canChangeClientScope && (
          <FoldoutMenuLink
            onClick={() => {
              setNavigationOpen(false);
              setSelectViewOpen(true);
            }}>
            Select View
            <Spacer />
            <FoldoutChevronContainer>
              <ChevronRight width={12} height={12} color={colors.black50} />
            </FoldoutChevronContainer>
          </FoldoutMenuLink>
        )}
        {profile.isAdmin && (
          <>
            <FoldoutMenuHeader>
              <FoldoutIconSlot>
                <GearIcon width={16} height={16} color={colors.black100} />
              </FoldoutIconSlot>
              Admin
            </FoldoutMenuHeader>
            {adminLinks.map((link) => (
              <AccessGuard key={link.to} accessor={link.accessor} strictMode={link.strictMode}>
                <FoldoutMenuLink
                  onClick={() => {
                    navigate(link.to);
                    setNavigationOpen(false);
                  }}>
                  {link.title}
                  <Spacer />
                  <FoldoutChevronContainer>
                    <ChevronRight width={12} height={12} color={colors.black50} />
                  </FoldoutChevronContainer>
                </FoldoutMenuLink>
              </AccessGuard>
            ))}
          </>
        )}
      </NavigationWrapper>
      <AccessGuard accessor={[PRODUCT_ACCESSORS.careCoordinationEnabled, PERMISSIONS.patientCreate]}>
        <AddButtonWrapper>
          <AddButton
            variant='outline-primary'
            onClick={() => {
              navigate('/patients/new');
              setNavigationOpen(false);
            }}>
            <PlusIcon color={colors.blue100} width={14} height={14} />
            Add Patient
          </AddButton>
        </AddButtonWrapper>
      </AccessGuard>
      <LogoutButton variant='ghost' onClick={() => logout()}>
        <LogoutIcon width={20} height={20} color={colors.blue100} />
        Logout
      </LogoutButton>
      <MobileNavFooter>
        <span>&copy; {new Date().getFullYear()} Olio Health</span>
        <FooterDot>&bull;</FooterDot>
        <span>Confidential</span>
        <FooterDot>&bull;</FooterDot>
        <PrivacyLink href='https://policy.olio.health/privacy-policy' target='_blank' rel='noreferrer'>
          Privacy Policy
        </PrivacyLink>
      </MobileNavFooter>
    </Foldout>
  );
}

const AddButton = styled(Button)`
  font-size: 14px;
  border-width: 1px;
  height: 40px;
  width: 100%;
`;

const AddButtonWrapper = styled.div`
  margin: 40px 0 8px;
  padding: 0 24px;
`;

const FoldoutMenuRow = styled.div`
  display: flex;
  cursor: pointer;
  padding: 16px 0;
  border-bottom: 1px solid var(--black-10);
  align-items: center;
  background-color: transparent;
  transition: background-color 0.25s;
  font-size: 14px;

  &:hover {
    background-color: var(--black-10);
  }
  &:active {
    background-color: var(--black-25);
  }
`;

const FoldoutMenuLink = styled(FoldoutMenuRow)`
  padding-left: 50px;
`;

const FoldoutMenuHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: var(--font-weight-medium);
  padding: 16px 0 16px 24px;
  border-bottom: 1px solid var(--black-10);
`;

const FoldoutIconSlot = styled.div`
  display: flex;
  padding-right: 10px;
`;

const FoldoutChevronContainer = styled.div`
  display: flex;
  padding-right: 24px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const FoldoutHeaderContainer = styled.div`
  display: flex;
  padding: 40px 24px;
  gap: 16px;
  align-items: center;
`;

const FoldoutHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const LogoutButton = styled(Button)`
  color: ${() => colors.blue100};
  margin: 32px auto;
`;

const UsernameText = styled.div`
  color: var(--primary-blue);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubtitleText = styled.span`
  color: var(--black-50);
`;

const ActingClientName = styled.span`
  font-style: italic;
  margin-left: 8px;
`;

const NavigationWrapper = styled.div`
  ${FoldoutMenuHeader}:not(:first-child) {
    margin-top: 40px;
  }
`;

const MobileNavFooter = styled.div`
  align-items: center;
  color: ${() => colors.black50};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 4px;
  justify-content: center;
  padding: 0 24px 12px;
`;
